import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import {
  CheckCircle, AlertTriangle, Loader, CreditCard, DollarSign,
  Calendar, Lock,
} from 'lucide-react';
import StripeCheckoutForm from '../components/StripeCheckoutForm';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * InstallmentPaymentPage — public, token-authenticated page for paying a
 * single installment chunk. Reached via the email link the admin sends:
 *
 *   /pay-installment/:token
 *
 * Flow:
 *   1. Load chunk + appointment + active providers from
 *      GET /api/installments/link/:token (no auth needed; the token IS the
 *      auth credential).
 *   2. Show chunk amount + appointment context + provider buttons.
 *   3. On provider selection:
 *      - Stripe: create intent via /stripe-intent, render StripeCheckoutForm,
 *        confirm card payment, then call /complete to mark the chunk paid.
 *      - PayPal: create order via /paypal-order, redirect to approvalUrl,
 *        on return capture via /paypal-capture, then call /complete.
 *      - Square: not yet wired here (admin has the existing Square Payment
 *        Link action as a fallback). Shows a "contact us" message.
 *   4. Show success state and stop accepting further attempts.
 *
 * No login required. The token is single-purpose and expires in 7 days.
 */

const InstallmentPaymentPage = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [stripePaymentIntentId, setStripePaymentIntentId] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);

  // Detect PayPal return: when the user finishes approving on PayPal we
  // come back here with ?paypal_order_id=... in the query string.
  const [paypalOrderId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('paypal_order_id') || null;
  });

  const loadInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/installments/link/${token}`);
      setInfo(res.data);
    } catch (err) {
      setLoadError(err.response?.data?.error || 'This payment link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  // Auto-capture PayPal on return.
  useEffect(() => {
    if (!paypalOrderId || completed || !info) return;
    (async () => {
      try {
        setCompleting(true);
        const captureRes = await axios.post(
          `${API_URL}/installments/link/${token}/paypal-capture`,
          { orderID: paypalOrderId }
        );
        if (captureRes.data?.success) {
          await markPaid('paypal', captureRes.data.orderId);
        } else {
          toast.error('PayPal capture failed');
        }
      } catch (err) {
        toast.error(err.response?.data?.error || 'PayPal capture failed');
      } finally {
        setCompleting(false);
        // Clean the query string so a refresh doesn't retry.
        window.history.replaceState({}, '', window.location.pathname);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paypalOrderId, info]);

  const markPaid = async (provider, paymentId) => {
    try {
      setCompleting(true);
      await axios.post(`${API_URL}/installments/link/${token}/complete`, {
        provider,
        payment_id: paymentId,
      });
      setCompleted(true);
      toast.success('Payment recorded. Thank you!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setCompleting(false);
    }
  };

  const startStripe = async () => {
    const stripeProvider = info.providers.find((p) => p.provider === 'stripe');
    if (!stripeProvider?.publishable_key) {
      toast.error('Stripe is not configured properly.');
      return;
    }
    try {
      setSelectedProvider('stripe');
      setStripePromise(loadStripe(stripeProvider.publishable_key));
      const res = await axios.post(`${API_URL}/installments/link/${token}/stripe-intent`);
      setStripeClientSecret(res.data.clientSecret);
      setStripePaymentIntentId(res.data.paymentIntentId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start Stripe payment');
      setSelectedProvider(null);
    }
  };

  const startPayPal = async () => {
    try {
      setSelectedProvider('paypal');
      const res = await axios.post(`${API_URL}/installments/link/${token}/paypal-order`);
      if (!res.data?.approvalUrl) throw new Error('No approval URL');
      // Tag the return URL so we know what to capture when the user comes back.
      const returnUrl = `${window.location.origin}${window.location.pathname}?paypal_order_id=${res.data.orderId}`;
      // Most PayPal approval pages have their own return URL; we navigate
      // directly so the browser comes back to our page on completion.
      window.location.href = res.data.approvalUrl + (res.data.approvalUrl.includes('?') ? '&' : '?')
        + 'return=' + encodeURIComponent(returnUrl);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start PayPal payment');
      setSelectedProvider(null);
    }
  };

  // ── Render states ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="flex items-center text-gray-500">
          <Loader className="animate-spin mr-2" size={20} />
          Loading payment details...
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
          <AlertTriangle className="mx-auto text-red-500 mb-4" size={48} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Payment Link Unavailable</h1>
          <p className="text-gray-600">{loadError}</p>
          <p className="text-xs text-gray-400 mt-6">
            If you believe this is a mistake, please contact the business that sent you this link.
          </p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
          <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Payment Received</h1>
          <p className="text-gray-600 mb-4">
            Thank you{info.client_first_name ? `, ${info.client_first_name}` : ''}! Your payment of{' '}
            <strong>${info.amount_due.toFixed(2)}</strong> for installment #{info.installment_number}
            {' '}has been received.
          </p>
          <p className="text-sm text-gray-500">You can close this window.</p>
        </div>
      </div>
    );
  }

  const apt = info.appointment;
  const onlineProviders = info.providers || [];
  const hasStripe = onlineProviders.some((p) => p.provider === 'stripe');
  const hasPayPal = onlineProviders.some((p) => p.provider === 'paypal');
  const hasSquare = onlineProviders.some((p) => p.provider === 'square');

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-sky-500 to-blue-600 px-6 py-5 text-white">
            <div className="flex items-center space-x-2 text-sky-100 text-sm mb-1">
              <Lock size={14} />
              <span>Secure payment</span>
            </div>
            <h1 className="text-2xl font-bold">
              Pay ${info.amount_due.toFixed(2)}
            </h1>
            <p className="text-sky-100 text-sm mt-1">
              Installment #{info.installment_number} for your upcoming appointment
            </p>
          </div>

          {/* Appointment context */}
          <div className="px-6 py-4 border-b bg-gray-50">
            <div className="text-xs uppercase text-gray-500 mb-2">Appointment</div>
            <div className="space-y-1 text-sm">
              {apt.pet_name && (
                <div><span className="text-gray-500">Pet:</span> <strong>{apt.pet_name}</strong></div>
              )}
              {apt.service_name && (
                <div><span className="text-gray-500">Service:</span> <strong>{apt.service_name}</strong></div>
              )}
              <div className="flex items-center text-gray-700">
                <Calendar size={14} className="mr-1.5 text-gray-400" />
                {apt.date} {apt.start_time && `at ${apt.start_time}`}
              </div>
              <div className="text-gray-500 pt-1 text-xs">
                Total appointment cost: ${apt.total_price.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Body: provider selection or active payment */}
          <div className="px-6 py-6">
            {!selectedProvider && (
              <>
                <h2 className="font-semibold text-gray-900 mb-3">Choose a payment method</h2>
                <div className="space-y-2">
                  {hasStripe && (
                    <button
                      onClick={startStripe}
                      disabled={completing}
                      className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition disabled:opacity-50"
                    >
                      <div className="flex items-center">
                        <CreditCard className="text-blue-600 mr-3" size={22} />
                        <div className="text-left">
                          <div className="font-semibold">Credit / Debit Card</div>
                          <div className="text-xs text-gray-500">Powered by Stripe</div>
                        </div>
                      </div>
                      <span className="text-sm text-gray-400">→</span>
                    </button>
                  )}
                  {hasPayPal && (
                    <button
                      onClick={startPayPal}
                      disabled={completing}
                      className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-yellow-500 hover:bg-yellow-50 transition disabled:opacity-50"
                    >
                      <div className="flex items-center">
                        <DollarSign className="text-yellow-600 mr-3" size={22} />
                        <div className="text-left">
                          <div className="font-semibold">PayPal</div>
                          <div className="text-xs text-gray-500">You'll be redirected to PayPal</div>
                        </div>
                      </div>
                      <span className="text-sm text-gray-400">→</span>
                    </button>
                  )}
                  {hasSquare && !hasStripe && !hasPayPal && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
                      Square is the only configured provider for this business. Please contact
                      the business directly to receive a Square payment link.
                    </div>
                  )}
                  {!hasStripe && !hasPayPal && !hasSquare && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-900">
                      No online payment provider is currently active. Please contact the business.
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-4 flex items-center">
                  <Lock size={11} className="mr-1" />
                  This link expires {info.expires_at ? new Date(info.expires_at).toLocaleDateString() : 'in 7 days'}.
                </p>
              </>
            )}

            {selectedProvider === 'stripe' && stripeClientSecret && stripePromise && (
              <Elements stripe={stripePromise} options={{ clientSecret: stripeClientSecret }}>
                <StripeCheckoutForm
                  amount={info.amount_due}
                  clientSecret={stripeClientSecret}
                  onSuccess={() => markPaid('stripe', stripePaymentIntentId)}
                  onCancel={() => {
                    setSelectedProvider(null);
                    setStripeClientSecret(null);
                    setStripePaymentIntentId(null);
                  }}
                />
              </Elements>
            )}

            {selectedProvider === 'stripe' && !stripeClientSecret && (
              <div className="flex items-center text-gray-500 py-8 justify-center">
                <Loader size={20} className="animate-spin mr-2" />
                Preparing card form...
              </div>
            )}

            {selectedProvider === 'paypal' && (
              <div className="flex items-center text-gray-500 py-8 justify-center">
                <Loader size={20} className="animate-spin mr-2" />
                Redirecting to PayPal...
              </div>
            )}

            {completing && (
              <div className="mt-4 flex items-center text-gray-500 justify-center">
                <Loader size={16} className="animate-spin mr-2" />
                Recording payment...
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 bg-gray-50 border-t text-xs text-gray-500 text-center">
            <Lock size={11} className="inline mr-1" />
            Your payment information is processed directly by your chosen provider and never stored on this server.
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallmentPaymentPage;
